import { useLayoutEffect, useState, type ReactNode } from "react";
import { usePersonalScope } from "../hooks/usePersonalScope";
import { bindPersonalScope } from "../utils/personalStorage";

/**
 * Puts the module-level stores kept per person (`keepPersonalState`) on the person and project
 * on screen, and renders the app under it only once they are on them. A store read before the
 * binding would show its empty, unbound part, and a component that remembers its first value
 * would then take the person's own state arriving a moment later for a change it must act on:
 * the Marvin drawer for a switch of thread, which drops the run in flight. So the children wait
 * for the binding, on a first mount and on every remount of this subtree (the tenant query
 * client remounts it when access changes), and for a new person or project. The binding runs in a
 * layout effect, never during render, so a render React abandons binds nobody; the children
 * render in the same commit's synchronous re-render, before anything is painted.
 */
export function PersonalScopeBinding({ children }: { children: ReactNode }) {
    const scope = usePersonalScope();
    const [bound, setBound] = useState<{ scope: string | null } | null>(null);
    useLayoutEffect(() => {
        bindPersonalScope(scope);
        setBound({ scope });
        return () => bindPersonalScope(null);
    }, [scope]);
    return bound?.scope === scope ? <>{children}</> : null;
}
